import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
} from '@yeshuman/sdk'
import { useState, useEffect } from 'react'
import { Activity, CreateActivityData, UpdateActivityData } from '../hooks/use-activities'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface ActivityFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  activity?: Activity | null
  onCreate: (data: CreateActivityData) => Promise<void>
  onUpdate: (data: UpdateActivityData) => Promise<void>
}

export function ActivityFormDialog({
  open,
  onOpenChange,
  activity,
  onCreate,
  onUpdate,
}: ActivityFormDialogProps) {
  const [activityType, setActivityType] = useState<'exercise' | 'sleep' | 'wellness'>('exercise')
  const [date, setDate] = useState('')
  const [notes, setNotes] = useState('')
  
  // Exercise fields
  const [exerciseTypeName, setExerciseTypeName] = useState('')
  const [exerciseDuration, setExerciseDuration] = useState('')
  
  // Sleep fields
  const [sleepStartTime, setSleepStartTime] = useState('')
  const [sleepDuration, setSleepDuration] = useState('')
  
  // Wellness fields
  const [wellnessTypeName, setWellnessTypeName] = useState('')
  const [wellnessDuration, setWellnessDuration] = useState('')
  
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form when activity is provided (edit mode)
  useEffect(() => {
    if (activity) {
      setActivityType(activity.type)
      setDate(activity.date.split('T')[0]) // Extract date part
      setNotes(activity.notes || '')
      
      if (activity.type === 'exercise') {
        setExerciseTypeName(activity.exercise_type || '')
        setExerciseDuration(activity.duration || '')
      } else if (activity.type === 'sleep') {
        setSleepStartTime(activity.sleep_start_time?.split('T')[0] + 'T' + activity.sleep_start_time?.split('T')[1].slice(0, 5) || '')
        setSleepDuration(activity.duration || '')
      } else if (activity.type === 'wellness') {
        setWellnessTypeName(activity.wellness_type || '')
        setWellnessDuration(activity.duration || '')
      }
    } else {
      // Reset form for create mode
      setDate(new Date().toISOString().split('T')[0])
      setNotes('')
      setExerciseTypeName('')
      setExerciseDuration('')
      setSleepStartTime('')
      setSleepDuration('')
      setWellnessTypeName('')
      setWellnessDuration('')
    }
  }, [activity, open])

  const parseDurationToISO = (durationStr: string): string => {
    if (!durationStr) return ''
    
    // If already ISO format, return as-is
    if (durationStr.startsWith('PT')) return durationStr
    
    // Try to parse natural language or time format
    // Examples: "30" (minutes), "1.5" (hours), "1:30" (hours:minutes)
    const hoursMatch = durationStr.match(/^(\d+\.?\d*)\s*(?:h|hr|hour|hours)?$/i)
    if (hoursMatch) {
      const hours = parseFloat(hoursMatch[1])
      const totalMinutes = Math.round(hours * 60)
      return `PT${totalMinutes}M`
    }
    
    const minutesMatch = durationStr.match(/^(\d+)\s*(?:m|min|minute|minutes)?$/i)
    if (minutesMatch) {
      return `PT${minutesMatch[1]}M`
    }
    
    // Try HH:MM format
    const timeMatch = durationStr.match(/^(\d+):(\d+)$/)
    if (timeMatch) {
      const hours = parseInt(timeMatch[1])
      const minutes = parseInt(timeMatch[2])
      return `PT${hours}H${minutes}M`
    }
    
    return durationStr
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (activity) {
        // Update mode
        const updateData: UpdateActivityData = {
          activity_id: activity.id,
          date,
          notes: notes || undefined,
        }

        if (activityType === 'exercise') {
          if (exerciseTypeName) updateData.exercise_type_name = exerciseTypeName
          if (exerciseDuration) updateData.duration = parseDurationToISO(exerciseDuration)
        } else if (activityType === 'sleep') {
          if (sleepStartTime) updateData.sleep_start_time = sleepStartTime
          if (sleepDuration) updateData.sleep_duration = parseDurationToISO(sleepDuration)
        } else if (activityType === 'wellness') {
          if (wellnessTypeName) updateData.wellness_type_name = wellnessTypeName
          if (wellnessDuration) updateData.wellness_duration = parseDurationToISO(wellnessDuration)
        }

        await onUpdate(updateData)
        toast.success('Activity updated successfully')
      } else {
        // Create mode
        const createData: CreateActivityData = {
          activity_type: activityType,
          date,
          notes: notes || undefined,
        }

        if (activityType === 'exercise') {
          if (!exerciseTypeName) {
            toast.error('Exercise type is required')
            setIsSubmitting(false)
            return
          }
          createData.exercise_type_name = exerciseTypeName
          if (exerciseDuration) createData.duration = parseDurationToISO(exerciseDuration)
        } else if (activityType === 'sleep') {
          if (!sleepStartTime) {
            toast.error('Sleep start time is required')
            setIsSubmitting(false)
            return
          }
          if (!sleepDuration) {
            toast.error('Sleep duration is required')
            setIsSubmitting(false)
            return
          }
          createData.sleep_start_time = sleepStartTime
          createData.sleep_duration = parseDurationToISO(sleepDuration)
        } else if (activityType === 'wellness') {
          if (!wellnessTypeName) {
            toast.error('Wellness type is required')
            setIsSubmitting(false)
            return
          }
          createData.wellness_type_name = wellnessTypeName
          if (wellnessDuration) createData.wellness_duration = parseDurationToISO(wellnessDuration)
        }

        await onCreate(createData)
        toast.success('Activity created successfully')
      }

      onOpenChange(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to save activity')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{activity ? 'Edit Activity' : 'Add Activity'}</DialogTitle>
          <DialogDescription>
            {activity ? 'Update your activity details' : 'Log a new health activity'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="activity-type">Activity Type *</Label>
            <Select
              value={activityType}
              onValueChange={(value: 'exercise' | 'sleep' | 'wellness') => setActivityType(value)}
              disabled={!!activity} // Don't allow changing type when editing
            >
              <SelectTrigger id="activity-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="exercise">Exercise</SelectItem>
                <SelectItem value="sleep">Sleep</SelectItem>
                <SelectItem value="wellness">Wellness</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <DatePicker
              date={date ? new Date(date) : undefined}
              onSelect={(d) => setDate(d ? d.toISOString().split('T')[0] : '')}
              placeholder="Select date"
            />
          </div>

          {activityType === 'exercise' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="exercise-type">Exercise Type *</Label>
                <Input
                  id="exercise-type"
                  placeholder="e.g., Running, Cycling, Swimming"
                  value={exerciseTypeName}
                  onChange={(e) => setExerciseTypeName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exercise-duration">Duration (optional)</Label>
                <Input
                  id="exercise-duration"
                  placeholder="e.g., 30m, 1h, 1.5h, PT30M"
                  value={exerciseDuration}
                  onChange={(e) => setExerciseDuration(e.target.value)}
                />
              </div>
            </>
          )}

          {activityType === 'sleep' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="sleep-start">Sleep Start Time *</Label>
                <Input
                  id="sleep-start"
                  type="datetime-local"
                  value={sleepStartTime}
                  onChange={(e) => setSleepStartTime(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sleep-duration">Duration *</Label>
                <Input
                  id="sleep-duration"
                  placeholder="e.g., 8h, 7.5h, PT8H"
                  value={sleepDuration}
                  onChange={(e) => setSleepDuration(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {activityType === 'wellness' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="wellness-type">Wellness Type *</Label>
                <Input
                  id="wellness-type"
                  placeholder="e.g., Meditation, Yoga, Mindfulness"
                  value={wellnessTypeName}
                  onChange={(e) => setWellnessTypeName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wellness-duration">Duration (optional)</Label>
                <Input
                  id="wellness-duration"
                  placeholder="e.g., 20m, 30m, PT20M"
                  value={wellnessDuration}
                  onChange={(e) => setWellnessDuration(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {activity ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

